/**
 * /calendar — Personal, persona-aware calendar.
 *
 * Auto-populated from existing data:
 *   - Dog birthdays (annual)
 *   - Heat cycles + predicted next heat (180d after last)
 *   - Litter whelping + derived breeding (whelp - 63d)
 *   - Transport pickup/delivery
 *   - Confirmed video tours
 * Plus user-added custom events (vet, training, dog show, etc.).
 *
 * Two views:
 *   - Month grid (default on desktop)
 *   - Agenda list (default on mobile)
 *
 * Web push reminder fires ~24h before any non-tentative event.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeftIcon as ArrowLeft, PlusIcon as Plus, CaretLeftIcon as CaretLeft, CaretRightIcon as CaretRight, CalendarIcon as CalendarIcon, CakeIcon as Cake, DropIcon as Drop, HeartStraightIcon as HeartStraight, BabyIcon as Baby, TruckIcon as Truck, MapPinLineIcon as MapPinLine, VideoCameraIcon as VideoCamera, PencilIcon as Pencil, TrashIcon as Trash, XIcon as X, ListBulletsIcon as ListBullets, GridFourIcon as GridFour, StorefrontIcon as Storefront, ChalkboardTeacherIcon as ChalkboardTeacher, UsersThreeIcon as UsersThree, LinkSimpleIcon as LinkSimple, BellRingingIcon as BellRinging } from "@phosphor-icons/react";
import { GoogleLogoIcon as GoogleLogo, AppleLogoIcon as AppleLogo, CopyIcon as Copy } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";
import { scheduleDeviceReminders, haptic } from "@/lib/native";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DateOfBirthInput from "@/components/DateOfBirthInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Icon name (from backend `source_meta.icon`) → Phosphor component.
const ICON_MAP = {
  Cake, Drop, DropHalf: Drop, HeartStraight, Baby, Truck, MapPinLine,
  VideoCamera, Calendar: CalendarIcon, Storefront, ChalkboardTeacher,
  UsersThree,
};

const KIND_LABEL = {
  vet: "Vet appointment",
  training: "Training",
  dog_show: "Dog show",
  grooming: "Grooming",
  vaccination: "Vaccination",
  medication: "Medication",
  heat_cycle: "Heat cycle",
  breeding: "Breeding",
  whelping: "Whelping",
  reminder: "Reminder",
  other: "Other",
};

// ---- Date helpers (local-time aware so calendar grids align visually) ----
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths    = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const monthLabel = (d) =>
  d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const dayHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Build the 6-row × 7-col grid of dates that cover the month view.
function buildMonthGrid(cursor) {
  const first = startOfMonth(cursor);
  const startDow = first.getDay();
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startDow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function Calendar() {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [sourceMeta, setSourceMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month");
  const [showAdd, setShowAdd] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null); // { url } | null
  const [editing, setEditing] = useState(null); // event being viewed (read-only or with edit/delete CTAs)
  const [editingForm, setEditingForm] = useState(null); // event being edited in form dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // id of event to confirm deletion
  const [whelpPrompt, setWhelpPrompt] = useState(null); // { breedingDate, dogId, title } — predicted whelp dialog
  const [dogs, setDogs] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?add=1 (often deep-linked from a dog/litter/transport page) — auto-open
  // the add dialog with optional seeded values.
  const seedFromUrl = useMemo(() => ({
    title: searchParams.get("seed_title") || "",
    date: searchParams.get("seed_date") || isoDay(new Date()),
    kind: searchParams.get("seed_kind") || "vet",
    dogId: searchParams.get("seed_dog") || "",
    notes: searchParams.get("seed_notes") || "",
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [searchParams.toString()]);

  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setShowAdd(true);
      // Strip the query so refresh doesn't re-trigger the modal.
      const next = new URLSearchParams(searchParams);
      ["add", "seed_title", "seed_date", "seed_kind", "seed_dog", "seed_notes"].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the calendar for cursor ±6 months so navigation feels instant.
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const start = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
      const end = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 1);
      const r = await api.get("/calendar", {
        params: { start: isoDay(start), end: isoDay(end) },
      });
      setEvents(r.data.events || []);
      setSourceMeta(r.data.source_meta || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load calendar.");
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Mint (or fetch) the personal .ics token and open the sync dialog
  // with one-tap Google / Apple buttons (plus copy fallback).
  const subscribeFeed = async () => {
    try {
      const r = await api.post("/calendar/feed-token");
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}${r.data.path}`;
      setSyncInfo({ url });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create the feed link.");
    }
  };

  // Fetch dogs once (for the "attach to dog" dropdown in the add modal).
  useEffect(() => {
    api.get("/dogs").then((r) => setDogs(r.data || [])).catch(() => setDogs([]));
  }, []);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const d = new Date(ev.date);
      const key = isoDay(d);
      (map[key] = map[key] || []).push(ev);
    }
    return map;
  }, [events]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      toast.success("Event removed.");
      setDeleteTarget(null);
      setEditing(null);
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="calendar-page">
      <Link to="/dashboard" className="text-sm text-[#6B6B6B] hover:text-[#0E0E0E] inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#0E0E0E]">Your calendar</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Heats, breedings, whelpings, transport jobs, video tours & anything else you add — in one place.
            <span className="hidden sm:inline"> Push reminders fire ~24h before each event.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-[#EAEAEA] p-0.5">
            <button
              type="button"
              data-testid="calendar-view-month"
              onClick={() => setView("month")}
              className={`px-3 py-1.5 text-xs rounded-full inline-flex items-center gap-1.5 ${view === "month" ? "bg-[#0E0E0E] text-white" : "text-[#6B6B6B]"}`}
            >
              <GridFour size={13} /> Month
            </button>
            <button
              type="button"
              data-testid="calendar-view-agenda"
              onClick={() => setView("agenda")}
              className={`px-3 py-1.5 text-xs rounded-full inline-flex items-center gap-1.5 ${view === "agenda" ? "bg-[#0E0E0E] text-white" : "text-[#6B6B6B]"}`}
            >
              <ListBullets size={13} /> Agenda
            </button>
          </div>
          <Button
            data-testid="calendar-subscribe-feed"
            variant="outline"
            onClick={subscribeFeed}
            className="rounded-full border-[#E5E5E5]"
            title={isNativeApp() ? "Sync with Google or Apple Calendar" : "Subscribe in Google or Apple Calendar"}
          >
            <LinkSimple size={15} className="mr-1.5" /> {isNativeApp() ? "Sync" : "Subscribe"}
          </Button>
          {isNativeApp() && (
            <Button
              data-testid="calendar-device-reminders"
              variant="outline"
              onClick={async () => {
                haptic("light");
                const now = new Date();
                const horizon = new Date(now.getTime() + 90 * 86400000);
                const upcoming = events
                  .filter((e) => {
                    const d = new Date(e.date);
                    return d > now && d <= horizon;
                  })
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .slice(0, 20)
                  .map((e, i) => {
                    // Fire at 9am the day before (or 9am same day when
                    // the event is tomorrow-or-sooner).
                    const d = new Date(e.date);
                    const at = new Date(d.getTime() - 86400000);
                    at.setHours(9, 0, 0, 0);
                    return {
                      id: 700001 + i,
                      title: e.title || "Upcoming event",
                      body: `Tomorrow: ${e.title || "calendar event"} — Armstrong K9 Registry`,
                      at: at > now ? at : new Date(now.getTime() + 60000),
                    };
                  });
                const res = await scheduleDeviceReminders(upcoming);
                if (res.ok) toast.success(`Device reminders set for ${res.count} upcoming event${res.count === 1 ? "" : "s"}.`);
                else if (res.reason === "denied") toast.message("Notifications are off — enable them in iOS Settings → Armstrong K9 Registry.");
                else if (res.reason === "empty") toast.message("No upcoming events in the next 90 days.");
                else toast.error("Couldn't set reminders on this device.");
              }}
              className="rounded-full border-[#E5E5E5]"
              title="Get on-device reminders the day before each event"
            >
              <BellRinging size={15} className="mr-1.5" /> Remind me
            </Button>
          )}
          <Button
            data-testid="calendar-add-event"
            onClick={() => setShowAdd(true)}
            className="bg-[#0E0E0E] hover:bg-[#1F1F1F] text-white rounded-full"
          >
            <Plus size={15} className="mr-1.5" /> Add event
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-5 text-[11px]">
        {Object.entries(sourceMeta).map(([key, meta]) => (
          <span key={key} className="inline-flex items-center gap-1.5 text-[#6B6B6B]">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
            {meta.label}
          </span>
        ))}
      </div>

      {view === "month" && (
        <MonthView
          cursor={cursor}
          setCursor={setCursor}
          eventsByDay={eventsByDay}
          sourceMeta={sourceMeta}
          onSelectEvent={setEditing}
          onAddForDay={(dayIso) => setShowAdd({ date: dayIso })}
          loading={loading}
        />
      )}

      {view === "agenda" && (
        <AgendaView events={events} sourceMeta={sourceMeta} onSelectEvent={setEditing} loading={loading} />
      )}

      {syncInfo && (
        <CalendarSyncDialog url={syncInfo.url} onClose={() => setSyncInfo(null)} />
      )}

      {showAdd && (
        <EventFormDialog
          dogs={dogs}
          seed={typeof showAdd === "object" && showAdd.date ? { ...seedFromUrl, date: showAdd.date } : seedFromUrl}
          onClose={() => setShowAdd(false)}
          onSaved={(breedingRow) => {
            setShowAdd(false);
            fetchEvents();
            if (breedingRow) {
              setWhelpPrompt({
                breedingDate: breedingRow.date,
                dogId: breedingRow.dog_id,
                title: breedingRow.title,
              });
            }
          }}
        />
      )}

      {editingForm && (
        <EventFormDialog
          dogs={dogs}
          existing={editingForm}
          onClose={() => setEditingForm(null)}
          onSaved={() => { setEditingForm(null); fetchEvents(); }}
        />
      )}

      {editing && (
        <EventDetailDialog
          event={editing}
          sourceMeta={sourceMeta}
          dogs={dogs}
          onClose={() => setEditing(null)}
          onEdit={editing.editable ? () => { setEditingForm(editing); setEditing(null); } : null}
          onDelete={editing.editable ? () => setDeleteTarget(editing.id) : null}
          onSaved={() => { setEditing(null); fetchEvents(); }}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="calendar-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the event from your calendar.
              Auto-derived events (heats, breedings, etc.) regenerate automatically
              and can't be deleted here — only your custom entries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="calendar-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="calendar-delete-confirm-btn"
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PredictedWhelpDialog
        prompt={whelpPrompt}
        onClose={() => setWhelpPrompt(null)}
        onCreated={() => { setWhelpPrompt(null); fetchEvents(); }}
      />
    </div>
  );
}


/**
 * Predicted-whelp confirm — appears once a breeder logs a breeding event.
 * Computes whelp date as breeding + 63 days (avg canine gestation), creates
 * a TENTATIVE whelping event with an explicit "estimate only" disclaimer in
 * both the title and the notes, plus the `tentative: true` flag so it
 * renders italicized in the UI.
 *
 * Disclaimer language is intentionally explicit per user request:
 * "I am not responsible for the accuracy of this prediction."
 */
function PredictedWhelpDialog({ prompt, onClose, onCreated }) {
  const open = !!prompt;
  const whelpDate = open
    ? (() => {
        const d = new Date(prompt.breedingDate);
        d.setUTCDate(d.getUTCDate() + 63);
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
      })()
    : "";
  const [creating, setCreating] = useState(false);
  const whelpDateLabelOut = open
    ? (() => {
        const d = new Date(prompt.breedingDate);
        d.setUTCDate(d.getUTCDate() + 63);
        return d.toLocaleDateString(undefined, {
          weekday: "long", month: "long", day: "numeric", year: "numeric",
          timeZone: "UTC",
        });
      })()
    : "";

  const handleConfirm = async () => {
    if (!prompt) return;
    setCreating(true);
    try {
      await api.post("/calendar/events", {
        title: `Predicted whelp — ${prompt.title.replace(/^bred\s*/i, "")} (estimate)`,
        date: whelpDate,
        kind: "whelping",
        all_day: true,
        dog_id: prompt.dogId || null,
        tentative: true,
        notes:
          "Estimate only. Calculated as 63 days from the breeding date " +
          "(average canine gestation; actual range is 58 to 68 days). " +
          "Armstrong K9 Registry is not responsible for the accuracy of this prediction. " +
          "Confirm with your veterinarian via ultrasound or progesterone testing.",
      });
      toast.success("Predicted whelp date added.");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add whelp date.");
      onClose();
    } finally {
      setCreating(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="calendar-predicted-whelp-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Add a tentative whelp date?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-[#6B6B6B]">
              <p>
                Based on a 63-day average gestation, your predicted whelp date is{" "}
                <strong className="text-[#0E0E0E]">{whelpDateLabelOut}</strong>.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900 text-xs">
                <strong className="block mb-1">Estimate only — not a medical prediction.</strong>
                Actual canine gestation ranges from 58 to 68 days. Armstrong K9
                Registry is not responsible for the accuracy of this date.
                Confirm with your veterinarian via ultrasound or progesterone
                testing for an actual due date.
              </div>
              <p className="text-xs">
                The event will be added in italics with a "tentative" badge so
                you and your buyers always know it's a guesstimate.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="predicted-whelp-cancel">No thanks</AlertDialogCancel>
          <AlertDialogAction
            data-testid="predicted-whelp-confirm"
            onClick={handleConfirm}
            disabled={creating}
            className="bg-[#0E0E0E] hover:bg-[#1F1F1F] text-white"
          >
            {creating ? "Adding…" : "Add tentative whelp date"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


// -------------------------------------------------------------------
// Month grid
// -------------------------------------------------------------------
function MonthView({ cursor, setCursor, eventsByDay, sourceMeta, onSelectEvent, onAddForDay, loading }) {
  const days = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const today = new Date();

  return (
    <div className="border border-[#EAEAEA] rounded-2xl bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#EAEAEA]">
        <button
          type="button"
          data-testid="calendar-prev-month"
          onClick={() => setCursor(addMonths(cursor, -1))}
          className="p-1.5 rounded-full hover:bg-[#FAFAFA] text-[#6B6B6B] hover:text-[#0E0E0E]"
        >
          <CaretLeft size={16} />
        </button>
        <h2 className="font-serif text-xl text-[#0E0E0E]">{monthLabel(cursor)}</h2>
        <button
          type="button"
          data-testid="calendar-next-month"
          onClick={() => setCursor(addMonths(cursor, 1))}
          className="p-1.5 rounded-full hover:bg-[#FAFAFA] text-[#6B6B6B] hover:text-[#0E0E0E]"
        >
          <CaretRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 border-b border-[#EAEAEA] text-[10px] uppercase tracking-wider text-[#6B6B6B] bg-[#FAFAFA]">
        {dayHeaders.map((h) => (
          <div key={h} className="px-2 py-2 text-center font-semibold">{h}</div>
        ))}
      </div>
      <div className="grid grid-cols-7" data-testid="calendar-grid">
        {days.map((d, idx) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const dayEvents = eventsByDay[isoDay(d)] || [];
          return (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              onClick={() => onAddForDay?.(isoDay(d))}
              onKeyDown={(e) => { if (e.key === "Enter") onAddForDay?.(isoDay(d)); }}
              className={`min-h-[72px] sm:min-h-[110px] border-r border-b border-[#EAEAEA] p-0.5 sm:p-1.5 last:border-r-0 cursor-pointer transition-colors group ${inMonth ? "bg-white hover:bg-[#FBF7EC]" : "bg-[#FAFAFA] hover:bg-[#F3EFE2]"}`}
              data-testid={`calendar-day-${isoDay(d)}`}
              aria-label={`Add event on ${isoDay(d)}`}
            >
              <div className="flex items-center justify-between mb-1 px-0.5">
                <span className={`text-[11px] ${isToday ? "bg-[#0E0E0E] text-white px-1.5 py-0.5 rounded-full font-bold" : inMonth ? "text-[#0E0E0E]" : "text-[#B8B8B8]"}`}>
                  {d.getDate()}
                </span>
                <span className="hidden sm:block text-[#C9A227] opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold leading-none" aria-hidden="true">+</span>
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((ev) => (
                  <EventPill key={ev.id} event={ev} sourceMeta={sourceMeta} onClick={() => onSelectEvent(ev)} />
                ))}
                {dayEvents.length > 3 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSelectEvent(dayEvents[3]); }}
                    className="text-[10px] text-[#6B6B6B] hover:text-[#0E0E0E] pl-1"
                  >
                    + {dayEvents.length - 3} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {loading && (
        <div className="p-6 text-center text-xs text-[#6B6B6B]">Loading…</div>
      )}
    </div>
  );
}

function EventPill({ event, sourceMeta, onClick }) {
  const meta = sourceMeta[event.source] || { color: "#525252", label: event.source };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={`calendar-event-${event.id}`}
      className="w-full text-left px-1 sm:px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] leading-tight hover:opacity-80 transition-opacity truncate"
      style={{ background: `${meta.color}1A`, color: meta.color, borderLeft: `3px solid ${meta.color}` }}
      title={event.title}
    >
      <span className={`block truncate ${event.tentative ? "italic" : ""}`}>{event.title}</span>
    </button>
  );
}


// -------------------------------------------------------------------
// Agenda list view
// -------------------------------------------------------------------
function AgendaView({ events, sourceMeta, onSelectEvent, loading }) {
  const now = new Date();
  // Group by month for headings; keep ascending order.
  const groups = useMemo(() => {
    const future = events.filter((e) => new Date(e.date) >= new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    const g = {};
    for (const ev of future) {
      const d = new Date(ev.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      (g[key] = g[key] || []).push(ev);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  if (loading) return <div className="p-6 text-center text-xs text-[#6B6B6B]">Loading…</div>;
  if (groups.length === 0) {
    return (
      <div className="border border-[#EAEAEA] rounded-2xl bg-white p-10 text-center" data-testid="calendar-empty">
        <CalendarIcon size={32} className="mx-auto text-[#B8B8B8] mb-3" />
        <p className="text-sm text-[#0E0E0E] font-medium">No upcoming events</p>
        <p className="text-xs text-[#6B6B6B] mt-1">
          Heat cycles, breedings and transport pickups appear here automatically.
          Tap <strong>Add event</strong> to schedule anything else.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map(([month, list]) => {
        const [y, m] = month.split("-");
        const heading = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
        return (
          <section key={month} data-testid={`agenda-month-${month}`}>
            <h3 className="font-serif text-lg text-[#0E0E0E] mb-2">{heading}</h3>
            <div className="border border-[#EAEAEA] rounded-2xl bg-white divide-y divide-[#EAEAEA] overflow-hidden">
              {list.map((ev) => (
                <AgendaRow key={ev.id} event={ev} sourceMeta={sourceMeta} onClick={() => onSelectEvent(ev)} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function AgendaRow({ event, sourceMeta, onClick }) {
  const meta = sourceMeta[event.source] || { color: "#525252", label: event.source, icon: "Calendar" };
  const Icon = ICON_MAP[meta.icon] || CalendarIcon;
  const d = new Date(event.date);
  const day = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
  const time = event.all_day ? "All day" : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`agenda-row-${event.id}`}
      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FAFAFA] transition-colors"
    >
      <div
        className="flex flex-col items-center justify-center w-14 h-14 rounded-xl flex-shrink-0"
        style={{ background: `${meta.color}1A`, color: meta.color }}
      >
        <Icon size={18} weight="regular" />
        <span className="text-[10px] font-semibold mt-0.5">{day.split(" ")[1]}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium text-[#0E0E0E] truncate ${event.tentative ? "italic" : ""}`}>
          {event.title}
        </p>
        <p className="text-xs text-[#6B6B6B] mt-0.5">
          {day} · {time} · <span style={{ color: meta.color }}>{meta.label}</span>
          {event.tentative && " · tentative"}
        </p>
      </div>
    </button>
  );
}


// -------------------------------------------------------------------
// Event form dialog — handles BOTH add (new event) and edit (existing).
// When `existing` is provided, PATCHes that event id; otherwise POSTs.
// -------------------------------------------------------------------
function EventFormDialog({ dogs, seed = null, existing = null, onClose, onSaved }) {
  const isEdit = !!existing;

  // For an existing event, derive the local-date/time using UTC accessors
  // because all-day events are stored at noon UTC. Time inputs use HH:MM.
  const initialDate = (() => {
    if (existing) {
      const d = new Date(existing.date);
      // For all-day events use the UTC date (the date is anchored at noon
      // UTC and reads as the same calendar day in any browser timezone).
      // For timed events use local date.
      if (existing.all_day) {
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
      }
      return isoDay(d);
    }
    return seed?.date || isoDay(new Date());
  })();
  const initialTime = (() => {
    if (existing && !existing.all_day) {
      return existing.time || (() => {
        const d = new Date(existing.date);
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      })();
    }
    return "";
  })();

  const [title, setTitle] = useState(() => existing?.title || seed?.title || "");
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [kind, setKind] = useState(() => {
    const k = existing?.kind || seed?.kind;
    return k && KIND_LABEL[k] ? k : "vet";
  });
  const [dogId, setDogId] = useState(() => existing?.dog_id || seed?.dogId || "");
  const [notes, setNotes] = useState(() => existing?.notes || seed?.notes || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Add a title.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        date,
        time: time || null,
        kind,
        notes: notes.trim(),
        dog_id: dogId || null,
        all_day: !time,
      };
      let savedRow;
      if (isEdit) {
        savedRow = (await api.patch(`/calendar/events/${existing.id}`, body)).data;
        toast.success("Event updated.");
      } else {
        savedRow = (await api.post("/calendar/events", body)).data;
        toast.success("Event added.");
      }
      // For NEW breeding events, hand the saved row up to the parent so it
      // can offer to auto-create a tentative whelp date 63 days later.
      onSaved(!isEdit && kind === "breeding" ? savedRow : null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={isEdit ? "calendar-edit-dialog" : "calendar-add-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0E0E0E]">
            {isEdit ? "Edit event" : "Add to calendar"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update any field below. Changes take effect immediately and reset any pending push reminder."
              : "Schedule a vet appointment, breeding date, training class, or any custom reminder. You'll get a push notification ~24h before the event."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              data-testid="calendar-form-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Vet — annual checkup"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <DateOfBirthInput data-testid="calendar-form-date" value={date} onChange={setDate} testId="calendar-form-date" autoComplete="off" ariaLabel="Event date" />
            </div>
            <div>
              <Label className="text-xs">Time (optional)</Label>
              <Input data-testid="calendar-form-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="calendar-form-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {dogs.length > 0 && (
            <div>
              <Label className="text-xs">Attach to dog (optional)</Label>
              <Select value={dogId || "none"} onValueChange={(v) => setDogId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="calendar-form-dog"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific dog</SelectItem>
                  {dogs.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name} ({d.breed})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              data-testid="calendar-form-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Bring previous bloodwork."
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="calendar-form-save"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0E0E0E] hover:bg-[#1F1F1F] text-white"
          >
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Save event")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------------------------------------------------------------------
// Event detail dialog (read-only for auto-derived, editable for custom)
// -------------------------------------------------------------------
function EventDetailDialog({ event, sourceMeta, onClose, onEdit, onDelete }) {
  const meta = sourceMeta[event.source] || { color: "#525252", label: event.source, icon: "Calendar" };
  const Icon = ICON_MAP[meta.icon] || CalendarIcon;
  const d = new Date(event.date);
  // For all-day events, format using UTC accessors so the date matches
  // the breeder's actual calendar day regardless of browser timezone.
  const dateLabel = event.all_day
    ? d.toLocaleDateString(undefined, {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
        timeZone: "UTC",
      })
    : d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const timeLabel = event.all_day ? "All day" : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="calendar-event-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-full"
              style={{ background: `${meta.color}1A`, color: meta.color }}
            >
              <Icon size={16} />
            </span>
            <DialogTitle className="font-serif text-xl text-[#0E0E0E]">{event.title}</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Event details for {event.title} on {dateLabel}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-[#6B6B6B]">
            <p>{dateLabel}</p>
            <p className="mt-0.5">{timeLabel}</p>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="px-2 py-0.5 rounded-full" style={{ background: `${meta.color}1A`, color: meta.color }}>
              {meta.label}
            </span>
            {event.tentative && (
              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                Tentative
              </span>
            )}
          </div>
          {event.notes && (
            <p className="text-[#6B6B6B] border-l-2 border-[#EAEAEA] pl-3 italic">{event.notes}</p>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            {event.url && (
              <Link
                to={event.url}
                className="inline-flex items-center gap-1.5 text-xs text-[#0E0E0E] underline underline-offset-2"
                data-testid="calendar-event-link"
              >
                Open details →
              </Link>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {onEdit && (
            <Button variant="outline" onClick={onEdit} data-testid="calendar-event-edit-btn">
              <Pencil size={14} className="mr-1.5" /> Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="outline" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash size={14} className="mr-1.5" /> Delete
            </Button>
          )}
          <Button onClick={onClose} className="bg-[#0E0E0E] hover:bg-[#1F1F1F] text-white">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------------------------------------------------------------------
// Calendar sync dialog — one-tap Google / Apple subscription
// -------------------------------------------------------------------
function CalendarSyncDialog({ url, onClose }) {
  const webcal = url.replace(/^https?/, "webcal");
  const googleHref = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`;
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Feed URL copied.");
    } catch {
      toast.info(url, { duration: 15000 });
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="calendar-sync-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0E0E0E]">Sync with your calendar</DialogTitle>
          <DialogDescription>
            Subscribe once and every heat, whelping, transport job, booking, and
            event you add here appears in your own calendar automatically — and
            stays up to date on its own.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <a
            href={googleHref}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 rounded-xl border border-[#EAEAEA] hover:border-[#0E0E0E] px-4 py-3 transition-colors"
            data-testid="calendar-sync-google"
          >
            <GoogleLogo size={20} weight="bold" className="text-[#4285F4] shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-ink">Google Calendar</p>
              <p className="text-[11px] text-[#6B6B6B]">Opens Google — click "Add" to subscribe. Google refreshes the feed every few hours.</p>
            </div>
          </a>
          <a
            href={webcal}
            className="flex items-center gap-3 rounded-xl border border-[#EAEAEA] hover:border-[#0E0E0E] px-4 py-3 transition-colors"
            data-testid="calendar-sync-apple"
          >
            <AppleLogo size={20} weight="fill" className="text-ink shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-ink">Apple Calendar</p>
              <p className="text-[11px] text-[#6B6B6B]">Opens the Calendar app on iPhone, iPad, or Mac and subscribes in one tap.</p>
            </div>
          </a>
          <button
            type="button"
            onClick={copyUrl}
            className="w-full flex items-center gap-3 rounded-xl border border-dashed border-[#D5D5D5] hover:border-[#0E0E0E] px-4 py-3 text-left transition-colors"
            data-testid="calendar-sync-copy"
          >
            <Copy size={18} className="text-[#6B6B6B] shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-ink">Copy feed URL</p>
              <p className="text-[11px] text-[#6B6B6B]">For Outlook or anything else: paste it under "Subscribe from web" / "From URL".</p>
            </div>
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="calendar-sync-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
